<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" style="height: 100%">
        <div class="activity-container object-in-a-sentence">
          <div class="select-image">
            <div class="phrase">
              <span>{{ currentRound.text }}</span>
            </div>
            <ul class="images">
              <li :key="answer.id" v-for="answer of currentRound.answers" class="image-container">
                <button @click="onSelectAnswer(answer)" class="pure-button">
                  <img
                    :alt="answer.answerImage.image.description"
                    :src="answer.answerImage.image.src"
                    :class="getImageClasses(answer.id)"
                  />
                </button>
              </li>
            </ul>
          </div>
          <article aria-live="assertive" class="typing" role="region">
            <div v-if="selectedAnswer" :transition="transitionsDisabled ? '' : 'fade-05'">
              <div class="round-text">
                <span>{{ currentRound.text }}&nbsp;</span>
              </div>
              <div class="color-block">
                <color-block-text :text="selectedAnswer.answer" :text-step="0"></color-block-text>
              </div>
              <span class="punctuation">{{ currentRound.punctuation }}</span>
            </div>
          </article>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';
import border from 'assets/svg/object-in-a-sentence/objectInASentenceBorder.svg';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    ColorBlockText
  },

  data() {
    return {
      selectedAnswer: null,
      transitionsDisabled: false,
      border
    };
  },

  methods: {
    handleNewRound() {
      this.transitionsDisabled = true;
      this.selectedAnswer = null;

      this.$nextTick(this.fadeInActivity);
    },

    async loadActivity() {
      await this.loadSounds();
      await this.loadImages();
      this.createRounds();
    },

    async loadImages() {
      const { rounds } = this.activityData;

      rounds.forEach(({ answers }) => {
        answers.forEach(({ answerImage }) => {
          this.queueImage(answerImage.image.src);
        });
      });
      return this.downloadImages();
    },

    async loadSounds() {
      const { rounds } = this.activityData;

      rounds.forEach(({ answers }) => {
        answers.forEach(({ answerAudio }) => {
          this.queueAudio(answerAudio.audio.src);
        });
      });
      await this.downloadAudio();
    },

    onSelectAnswer(answer) {
      if (this.selectedAnswer?.id === answer.id) return;

      this.blurImgButtons();

      const self = this;
      TweenMax.to('.select-image', 1, {
        top: '35%',
        onComplete() {
          self.enableKeyboard();
          self.overwriteRoundSteps({
            roundId: self.currentRound.id,
            steps: [{ answer: answer.answer }]
          });
          self.setTypingDistanceUnderSelectImage();
          self.selectedAnswer = answer;
          self.setScreenReaderAlert(
            `The sentence "${self.currentRound.text} ${answer.answer}." was added to the page.`
          );
          self.overwriteProgressText({ currentText: answer.answer });
          self.setTotalKeyboardingSegment(answer.answer);
        }
      });
    },

    getImageClasses(id) {
      const baseClasses = ['image', 'image-option'];
      if (!this.selectedAnswer) return baseClasses;

      return id === this.selectedAnswer.id
        ? [...baseClasses, 'image-selected']
        : [...baseClasses, 'image-disabled'];
    },

    fadeInActivity() {
      this.$nextTick(this.setActivityContainerHeight);
      this.$nextTick(() => {
        TweenMax.to('.object-in-a-sentence', 0.5, { opacity: 1 });
        this.transitionsDisabled = false;
      });
    },

    startActivity() {
      this.showKeyboard({ enable: false });
      this.setupNewRound();
      this.errorLoggerStart();

      TweenMax.delayedCall(0.5, this.fadeInActivity);
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.selectedAnswer) {
          this.cache.get(this.selectedAnswer.answerAudio.audio.src).play();
        }

        if (this.isEndOfActivity) {
          TweenMax.delayedCall(2, this.completeActivity);
          return;
        }

        TweenMax.delayedCall(1, () => {
          TweenMax.to('.object-in-a-sentence', 0.5, {
            opacity: 0,
            onComplete() {
              TweenMax.to('.select-image', 0, { top: '50%' });
            }
          });
        });

        TweenMax.delayedCall(2, () => {
          this.setTotalKeyboardingSegment('');
          this.disableKeyboard();
          this.handleNewRound();
          this.setupNewRound();
        });
      }
    },

    blurImgButtons() {
      const imgButtonElems = document.querySelectorAll('.image-container button');

      imgButtonElems?.forEach(imgElem => {
        imgElem.blur();
      });
    },

    setTypingDistanceUnderSelectImage() {
      const selectImageEl = document.getElementsByClassName('select-image')?.[0];
      const typingEl = document.getElementsByClassName('typing')?.[0];

      if (selectImageEl && typingEl) {
        typingEl.style.top = `calc(17.5% + ${selectImageEl.offsetHeight + 48}px)`;
      }
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;

      const keyboardConfig = {
        keyboardType,
        roofs
      };

      return keyboardConfig;
    }
  },

  mounted() {
    window.addEventListener('resize', this.setTypingDistanceUnderSelectImage);
  },

  beforeDestroy() {
    window.addEventListener('resize', this.setTypingDistanceUnderSelectImage);
  },

  watch: {
    expectedKey(newExpectedKey) {
      if (newExpectedKey) {
        let instructions = `Type the ${newExpectedKey} key.`;

        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.select-image {
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: absolute;
  height: auto;
  transform: translateY(-50%);
  width: 100%;
  top: 50%;
  left: 0;

  .phrase {
    padding-left: 20px;
    padding-right: 5px;

    span {
      max-width: 400px;
    }
  }
  .images {
    list-style: none;
    margin: 0px;
    padding-left: 5px;
    padding-right: 20px;
  }

  .phrase,
  .images {
    align-items: center;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
  }
}

.punctuation,
.round-text,
.color-block,
.phrase {
  font-weight: normal;
  display: inline-block;
  font-family: 'hwtprint';
  font-size: 26px;
}

.typing {
  position: absolute;
  text-align: center;
  top: 50%;
  width: 100%;
}

.image-container {
  position: relative;
  margin: 0 5px;

  .image {
    transition: 0.5s all;
    border: 2px dashed #aaaaaa;
    border-radius: 8px;
  }
  .image-selected {
    border: 2px solid #000000;
    border-radius: 8px;
  }
  .image-option {
    cursor: pointer;
    height: auto;
    max-width: 160px;
    width: 100%;

    @media (max-width: 690px) {
      max-width: 130px;
    }
  }
}

.object-in-a-sentence {
  height: 100%;
  min-height: 400px;
  opacity: 0;
  position: relative;

  @media (max-width: 432px) {
    min-height: 600px;
  }
}

.chrome-os {
  .object-in-a-sentence {
    min-height: 300px;
  }
}

.tablet {
  .object-in-a-sentence {
    min-height: auto;
  }
}
</style>
